import React from "react"
import { Spinner, Page } from "components/ui";
import { Button, useMediaQuery } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchNotificationSettings,
  updateNotificationSettings,
} from "actions/notifications";
import { Formik, Form } from "formik";
import { CheckboxInput } from "components/form";
import selectors from "selectors";
import useTranslate from "hooks/use-translate-language";
import useDocumentTitle from "hooks/use-document-title";
import "./notification-settings.scss";

import BreadCrumbs from "components/ui_new/breadcrumbs";
import { useKeyboardNavigation } from "./use-keyboard-navigation";

const NotificationSettings = () => {

  const dispatch = useDispatch();
  const [isFetching, setIsFetching] = React.useState(false);
  const translate = useTranslate();
  const formikRef = React.useRef(null);
  const isSmallScreen = useMediaQuery("(max-width: 800px)");
  const saveButtonRef = React.useRef(null);
  const clearButtonRef = React.useRef(null);
  const notificationSettings = useSelector(
    selectors.getPatientNotificationSettings
  );

  useDocumentTitle(translate("notificationSettings"));

  useKeyboardNavigation({
    saveButtonRef,
    clearButtonRef
  });

  React.useEffect(() => {
    setIsFetching(true);
    dispatch(fetchNotificationSettings()).finally(() => {
      setIsFetching(false);
    });
  }, [dispatch]);

  return (
    <Page.Container>
      <Page.Header
        title={translate("notificationSettings")}
        goBack
      />
      <Page.Content
        isFetching={isFetching}
      >
        <div className="notification-settings-screen">

          <div className="notification-settings-header" tabIndex={-1}>
            <BreadCrumbs childLabel={translate("notificationSettings")} parentLink="/profile" parentLinkLabel={translate("profile")} title={translate("notificationSettings")} titleClassname="notification-settings-title" />
            {!isSmallScreen && (
              <div className="notification-settings-buttons-container">
                <Button
                  ref={clearButtonRef}
                  id="clear-all-button"
                  variant="outlined"
                  onClick={() => formikRef.current?.resetForm()}
                  className="ui_button"
                  aria-label={translate("clearAll")}
                  tabIndex={-1}
                >
                  {translate("clearAll")}
                </Button>
                <Button
                  ref={saveButtonRef}
                  id="save-notification-button"
                  variant="contained"
                  onClick={() => formikRef.current?.submitForm()}
                  className="ui_button"
                  aria-label={translate("save")}
                  tabIndex={-1}
                >
                  {translate("save")}
                </Button>
              </div>
            )}

          </div>
          <h2 className="notification-settings-subtitle" tabIndex={0}>{translate("notificationsMessage")}</h2>

          <div className="notification-settings-content">
            {isFetching ? (
              <div className="loading-container">
                <Spinner />
              </div>
            ) : (
              // <div className="form-container">
                <Formik
                  innerRef={formikRef}
                  onSubmit={async (values) => {
                    await dispatch(updateNotificationSettings(values));
                  }}
                  initialValues={{ ...notificationSettings }}
                >
                  {() => (
                    <Form className="form-wrapper">
                      <div className="notifications-column">
                        <CheckboxInput
                          name="sms"
                          label={translate("smsMessages")}
                          className="notification-setting"
                        />
                        <CheckboxInput
                          name="email"
                          label={translate("email")}
                          className="notification-setting"
                        />
                        <CheckboxInput
                          name="push_notifications"
                          label={translate("pushNotifications")}
                          className="notification-setting"
                        />
                      </div>

                      <div className="options-column">
                        <CheckboxInput
                          name="options.video_chat"
                          label={translate("videoChats")}
                          className="notification-setting"
                        />
                        <CheckboxInput
                          name="options.text_chat"
                          label={translate("textChats")}
                          className="notification-setting"
                        />
                        <CheckboxInput
                          name="options.questionnaires"
                          label={translate("questionnaires")}
                          className="notification-setting"
                        />
                        <CheckboxInput
                          name="options.file_upload"
                          label={translate("fileUploads")}
                          className="notification-setting"
                        />
                        <CheckboxInput
                          name="options.profile_update"
                          label={translate("profileUpdates")}
                          className="notification-setting"
                        />
                      </div>
                    </Form>
                  )}
                </Formik>
              // </div>
            )}
          </div>

          {isSmallScreen && (
            <div className="notification-settings-buttons-container">
              <Button
                ref={saveButtonRef}
                id="save-notification-button"
                variant="contained"
                onClick={() => formikRef.current?.submitForm()}
                className="ui_button"
                aria-label={translate("save")}
                tabIndex={-1}
              >
                {translate("save")}
              </Button>
              <Button
                ref={clearButtonRef}
                id="clear-all-button"
                variant="outlined"
                onClick={() => formikRef.current?.resetForm()}
                className="ui_button"
                aria-label={translate("clearAll")}
                tabIndex={-1}
              >
                {translate("clearAll")}
              </Button>
            </div>
          )}

        </div>
      </Page.Content>
    </Page.Container>
  );
};

export default NotificationSettings;